import { Box, BrandIcon, Button, Content } from '@acsbe/ui';
import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useApplicationViewState } from '../../context';
import { useMerchantSession } from '../../context/MerchantSessionContext';
import { useAnalytics } from '../../services/analytics';
import {
  LOGIN_COMPLETED,
  SIGNUP_WIZARD_STEP_COMPLETED,
} from '../../services/analytics/event-names';
import { useGoogleLogin } from '../../services/social-login';
import {
  BasicForm,
  IBasicFormContext,
  MESSAGE_EVENT,
  sendMessageToWrapper,
} from '../../utils';
import { AUTH_TYPE, markUserLoggedIn } from '../../utils/auth-utils';
import axios from '../../utils/axiosInstance';

export const GOOGLE_LOGIN_INTENT = 'google';

interface GoggleButtonProps {}

export const GoogleButton = (props: GoggleButtonProps) => {
  const { t } = useTranslation();
  const errorId = useId();
  const formRef = useRef<IBasicFormContext<unknown>>(null);
  const [googleResponse, setGoogleResponse] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const analytics = useAnalytics();
  const { hash, search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const redirect = searchParams.get('redirect');
  const { merchantDetails, setAccessibeUser } = useMerchantSession();
  const {
    SignupWizard: { stepOneCompletedWithSocial },
  } = useApplicationViewState();

  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      setGoogleResponse(codeResponse.code);
      onSubmit({
        code: codeResponse.code,
        intent: GOOGLE_LOGIN_INTENT,
        hash,
        redirect,
      });
    },
    onError: () => {
      setShowError(true);
    },
    flow: 'auth-code',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (data: any) => {
    const intent = data.intent;

    const hash = data.hash?.toString();
    const redirectTo = data.redirect?.toString() ?? undefined;

    switch (intent) {
      case GOOGLE_LOGIN_INTENT: {
        const code = data.code?.toString();
        if (!code) {
          return false;
        }
        try {
          setErrorMessage(null);
          setShowError(false);
          const authResponse = await axios.post('/api/accessibe/google-login', {
            code,
          });
          const { tempSocialLoginId } = authResponse.data.data;
          if (tempSocialLoginId) {
            stepOneCompletedWithSocial(tempSocialLoginId);
            analytics.mixpanel.trackEvent(SIGNUP_WIZARD_STEP_COMPLETED, {
              step: 'GoogleSignup',
              pluginSource: merchantDetails?.source,
              tempSocialLoginId,
            });
            return false;
          }

          const response = await axios.get('/api/accessibe/get-user', {
            withCredentials: true,
          });

          markUserLoggedIn(AUTH_TYPE.LOGIN);
          const userData = {
            email: response.data.email,
            businessName: response.data.account?.name,
            fullName: response.data.name,
            phoneNumber: response.data.phone,
            countryCode: response.data.country,
            userId: response.data._id,
            accountId: response.data.account?._id,
          };
          setAccessibeUser({
            userId: userData.userId,
            email: userData.email,
            fullName: userData.fullName,
            activeAccountId: userData.accountId,
            phoneNumber: response.data.phone,
            countryCode: response.data.country,
          });
          sendMessageToWrapper(MESSAGE_EVENT.LOGIN, userData);

          analytics.mixpanel.identify(userData.userId);

          analytics.mixpanel.trackEvent(LOGIN_COMPLETED, {
            loginType: 'google',
            pluginSource: merchantDetails?.source,
            email: userData.email,
            fullName: userData.fullName,
            userId: userData.userId,
          });
          return;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          if (
            error.response &&
            error.response?.data?.message === 'EMAIL_NOT_ALLOWED'
          ) {
            setErrorMessage(
              t('EMAIL_NOT_ALLOWED', {
                source: merchantDetails?.source ?? 'CMS',
              }),
            );
            setShowError(true);
          }
          return false;
        }
      }
      default: {
        try {
          // await context.auth.login(identity.email, identity.password);
          // await context.queryClient.invalidateQueries(['me']);
          // context.queryClient.removeQueries(['me']);
          // return processLoginRedirect(new URL(params.request.url), hash);
        } catch (err) {
          // if (err instanceof FormFieldValidationError) {
          //   return simpleError<ILogin>(err.field! as keyof ILogin, err.message);
          // }
          // return simpleError<ILogin>('email', 'WRONG_PASSWORD');
        }
      }
    }
  };

  // useEffect(() => {
  //   if (googleResponse) {
  //     console.log('value is');
  //     console.log(formRef.current);
  //     formRef.current?.submit();
  //   }
  // }, [googleResponse]);

  return (
    <BasicForm
      ref={formRef}
      onGeneralError={() => {
        setShowError(true);
      }}
    >
      {({ isSubmitting }) => {
        return (
          <>
            <input type="hidden" name="redirect" value={redirect ?? ''} />
            <input type="hidden" name="hash" value={hash} />
            <input type="hidden" name="intent" value={GOOGLE_LOGIN_INTENT} />
            {googleResponse && (
              <input type="hidden" name="code" value={googleResponse} />
            )}
            <StyledButton
              type="button"
              scale="base"
              variant="primary"
              fullWidth
              onClick={googleLogin}
              data-mixpanel="google-login"
              disabled={isSubmitting}
              aria-describedby={errorId}
              showError={showError}
            >
              <BrandIcon name="Google" />
              <span>{t('CONTINUE_WITH_GOOGLE')}</span>
            </StyledButton>
            <div aria-live="polite" id={errorId}>
              {showError && (
                <Box marginBlockStart={2}>
                  <Content scale="danger">
                    {errorMessage ?? t('UNKNOWN_ERROR')}
                  </Content>
                </Box>
              )}
            </div>
          </>
        );
      }}
    </BasicForm>
  );
};

const StyledButton = styled(Button)<{ showError: boolean }>`
  ${(props) =>
    props.showError === true
      ? 'border: 1px solid #D8161F; background: #FDE8E9;'
      : 'border: 2px solid var(--Color-Grey-400, #CBD2E1); background: var(--Color-Foundations-White, #FFF);'};
`;
